import React from 'react';
import PropTypes from 'prop-types';
import { noop } from 'underscore';
import { keyName } from '@bingads-webui/keycode';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Input, Glyphicon } from '@bingads-webui-react/primitive';
import { getStyles } from './getStyles';

/**
 * SearchBox component provides an input field for searching through content
 */

class SearchBoxUnThemed extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      showForwardIcon: !!this.props.value,
    };
  }

  componentWillUnmount() {
    this.props.onDestroy();
  }

  onKeyDown = (event) => {
    // enter
    if (event.key === keyName[13]) {
      event.preventDefault();
      this.props.onSearch(this.props.value);
    }

    // escape
    if (event.key === keyName[0x1B]) {
      event.preventDefault();
      this.props.onQueryChange('');
    }
  };

  onClick = (event) => {
    event.preventDefault();
    this.props.onSearch(this.props.value);
  };

  onChange = (event) => {
    this.props.onQueryChange(event.target.value);

    const showForwardIcon = !!event.target.value;

    this.setState({
      showForwardIcon,
    });
  };

  render() {
    const { showForwardIcon } = this.state;
    const forwardIconStyle = {
      visibility: showForwardIcon ? 'visible' : 'hidden',
    };
    return (
      <div className={`search-box ${this.props.searchBoxClasses.join(' ')}`} role={this.props.role}>
        <div className={this.props.classes.inputGroup}>
          <Glyphicon
            className={this.props.classes.logo}
            glyph="ba-Search"
          />
          <Input
            // eslint-disable-next-line no-nested-ternary
            className={this.props.isForListBox ?
              this.props.classes.inputForListBox :
              this.props.renderExtraContent ?
              this.props.classes.inputForHotKey : this.props.classes.input}
            maxLength={this.props.maxLength}
            placeholder={this.props.placeholder}
            onKeyDown={this.onKeyDown}
            onFocus={this.props.onFocus}
            value={this.props.value}
            onChange={this.onChange}
            disabled={this.props.isDisabled}
            aria-label={this.props.ariaLabel || this.props.placeholder}
          />
          {this.props.renderExtraContent}
          {this.props.showArrowIcon && (<Glyphicon
            className={`${this.props.classes.icon} ${this.props.iconClasses.join(' ')}`}
            glyph="ba-Forward"
            onClick={this.onClick}
            onKeyPress={this.onClick}
            style={forwardIconStyle}
            tabIndex={0}
            data-is-focusable="true"
            role="button"
            aria-label={this.props.arrowIconAriaLabel}
          />)}
        </div>
      </div>
    );
  }
}

SearchBoxUnThemed.propTypes = {
  /**
   * the maximum length of the search text
   */
  maxLength: PropTypes.number,
  /**
   * Gets called when user clicks search icon and preses 'Enter' key.
   * @param {string} - input query
   */
  onSearch: PropTypes.func,
  /**
   * Gets called when unmount the control.
   */
  onDestroy: PropTypes.func,
  /**
   * Gets called when focus the control.
   */
  onFocus: PropTypes.func,
  /**
   * Gets called when user inputs
   * @param {string} - input query
   */
  onQueryChange: PropTypes.func.isRequired,
  /**
   * the placeholder for the search box
   */
  placeholder: PropTypes.string,
  /**
   * the extra content need to render for the search box
   */
  renderExtraContent: PropTypes.element,
  /**
   * show arrow icon or not
   */
  showArrowIcon: PropTypes.bool,
  /**
   * disable the input box or not
   */
  isDisabled: PropTypes.bool,
  /**
   * Is the search-box for list box
   */
  isForListBox: PropTypes.bool,
  /**
   * input box value
   */
  value: PropTypes.string,
  /**
   * the classes to assign to the icon's
   * span element. This is helpful if glyphicon is to be used. Then two classes must be
   * passed: class of the icon, and general glyphicon class.
   */
  iconClasses: PropTypes.arrayOf(PropTypes.string),
  /**
   * the classed to assign the overall style to the component
   */
  searchBoxClasses: PropTypes.arrayOf(PropTypes.string),
  /**
   * class names that come from jss theme or default style
   */
  classes: PropTypes.shape({
    logo: PropTypes.string,
    input: PropTypes.string,
    inputForHotKey: PropTypes.string,
    inputForListBox: PropTypes.string,
    icon: PropTypes.string,
    inputGroup: PropTypes.string,
  }).isRequired,
  /**
   * aria-label of search box
   */
  ariaLabel: PropTypes.string,
  /**
   * role of search box
   */
  role: PropTypes.string,
  /**
   * aria-label of arrow icon
   */
  arrowIconAriaLabel: PropTypes.string,
};

SearchBoxUnThemed.defaultProps = {
  maxLength: 150,
  placeholder: '',
  isDisabled: false,
  isForListBox: false,
  iconClasses: [],
  searchBoxClasses: [],
  onDestroy: noop,
  onFocus: noop,
  value: '',
  onSearch: noop,
  ariaLabel: null,
  renderExtraContent: undefined,
  showArrowIcon: true,
  role: undefined,
  arrowIconAriaLabel: 'Search',
};

export const SearchBox = withDefaultStyles(SearchBoxUnThemed, getStyles);
